import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import "react-pro-sidebar/dist/css/styles.css";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import WalletOutlinedIcon from "@mui/icons-material/WalletOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useTranslation } from "react-i18next";

// Components
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import Plans from "../modals/Plans";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

const Item = ({ title, to, icon, selected, setSelected, onClick, id }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleClick = () => {
    setSelected(title);
    if (onClick) {
      onClick();
    }
    trackEvent(`sidebar-${id}`, {
      item_name: title,
      item_id: title.toLowerCase().replace(" ", "-") + id,
    });
  };

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={handleClick}
      icon={icon}
      id={id}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 800);
  const [selected, setSelected] = useState(t("sidebar.dashboard"));
  const location = useLocation();
  const [accountData, setAccountData] = useState(null);
  const { showNotification } = useNotification();
  const [plansModalOpen, setPlansModalOpen] = useState(false);

  useEffect(() => {
    const fetchPlanData = async () => {
      const { data, error } = await supabase
        .from("accounts")
        .select(
          `
              team_id(
                plan(
                  premium_support
                ),
                stripe_id
              ),
              roles
            `
        )
        .single();

      if (!error) {
        setAccountData(data);
      }
    };

    fetchPlanData();
  }, []);

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const lastPart = pathParts[pathParts.length - 1];
    const pathWithoutVariables = lastPart.split("?")[0];
    switch (pathWithoutVariables) {
      case "dashboard":
        setSelected(t("sidebar.whatsapp"));
        break;
      case "messages":
        setSelected(t("sidebar.messages"));
        break;
      case "contacts":
        setSelected(t("sidebar.contacts"));
        break;
      case "templates":
        setSelected(t("sidebar.templates"));
        break;
      case "files":
        setSelected(t("sidebar.files"));
        break;
      case "ai":
        setSelected(t("sidebar.ai"));
        break;
      case "qualitas":
        setSelected(t("sidebar.qualitas"));
        break;
      case "profile":
        setSelected(t("sidebar.profile"));
        break;
      case "whatsapp":
        setSelected(t("sidebar.whatsapp"));
        break;
      case "subscription":
        setSelected(t("sidebar.subscription"));
        break;
      case "faq":
        setSelected(t("sidebar.faq"));
        break;
      case "tutorials":
        setSelected(t("sidebar.tutorials"));
        break;
      default:
        setSelected(t("sidebar.dashboard"));
    }
  }, [location, t]);

  const handleSupportClick = () => {
    if (accountData?.team_id?.plan?.premium_support) {
      const supportText = t("sidebar.supportWhatsAppText", {
        account: accountData?.team_id?.stripe_id,
      });
      const encodedText = encodeURIComponent(supportText);
      window.open(`https://wa.me/17788078010?text=${encodedText}`, "_blank");
    } else {
      showNotification(t("sidebar.supportNotIncludedInPlan"), "warning");
      setPlansModalOpen(true);
    }
  };

  const handleClosePlansModal = () => {
    setPlansModalOpen(false);
  };

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: theme.palette.mode === 'dark'
            ? "rgba(20, 27, 45, 0.6) !important"
            : "rgba(255, 255, 255, 0.8) !important",
          backdropFilter: "blur(20px)",
          borderRight: theme.palette.mode === 'dark'
            ? "1px solid rgba(255,255,255,0.05)"
            : "1px solid rgba(0,0,0,0.05)",
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "8px 20px 8px 15px !important",
          borderRadius: "8px !important",
          margin: "4px 12px !important",
          transition: "all 0.2s ease-in-out !important",
        },
        "& .pro-inner-item:hover": {
          color: theme.palette.mode === 'dark' ? "#868dfb !important" : "#535ac8 !important",
          background: theme.palette.mode === 'dark' ? "rgba(134, 141, 251, 0.1) !important" : "rgba(83, 90, 200, 0.1) !important",
        },
        "& .pro-menu-item.active .pro-inner-item": {
          color: "#fff !important",
          background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%) !important",
          boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  component="a"
                  href="https://getkoubi.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    color: "black !important",
                    "&:visited": { color: "black !important" },
                  }}
                >
                  Koubi
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* User profile (commented out) */}
          {/* {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={`../../assets/user.png`}
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  Bernardo Ruz
                </Typography>
              </Box>
            </Box>
          )} */}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            {!isCollapsed && (
              <Typography
                variant="h6"
                color={colors.grey[300]}
                sx={{ m: "15px 0 5px 20px" }}
              >
                {t("sidebar.dashboard")}
              </Typography>
            )}

            <Item
              title={t("sidebar.messages")}
              to="/dashboard/messages"
              icon={<MessageOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="messages"
            />
            <Item
              title={t("sidebar.contacts")}
              to="/dashboard/contacts"
              icon={<ContactsOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="contacts"
            />
            <Item
              title={t("sidebar.templates")}
              to="/dashboard/templates"
              icon={<TextSnippetOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="templates"
            />
            <Item
              title={t("sidebar.files")}
              to="/dashboard/files"
              icon={<AddPhotoAlternateOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="files"
            />
            <Item
              title={t("sidebar.ai")}
              to="/dashboard/ai"
              icon={<AutoAwesomeOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="ai"
            />
            {accountData?.roles?.includes("qualitas-user") && (
              <Item
                title={"Qualitas"}
                to="/dashboard/qualitas"
                icon={<SupportAgentOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
                id="qualitas"
              />
            )}
            {!isCollapsed && (
              <Typography
                variant="h6"
                color={colors.grey[300]}
                sx={{ m: "15px 0 5px 20px" }}
              >
                {t("sidebar.account")}
              </Typography>
            )}

            <Item
              title={t("sidebar.profile")}
              to="/dashboard/profile"
              icon={<PersonOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="profile"
            />
            <Item
              title={t("sidebar.whatsapp")}
              to="/dashboard/whatsapp"
              icon={<WhatsAppIcon />}
              selected={selected}
              setSelected={setSelected}
              id="whatsapp"
            />
            <Item
              title={t("sidebar.subscription")}
              to="/dashboard/subscription"
              icon={<WalletOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="subscription"
            />
            <Item
              title={t("sidebar.faq")}
              to="/dashboard/faq"
              icon={<HelpOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              id="faq"
            />
            <Item
              title={t("sidebar.tutorials")}
              to="/dashboard/tutorials"
              icon={<VideoLibraryIcon />}
              selected={selected}
              setSelected={setSelected}
              id="tutorials"
            />
            <Item
              title={t("sidebar.support")}
              icon={<SupportAgentIcon />}
              selected={selected}
              setSelected={setSelected}
              onClick={handleSupportClick}
              id="support"
            />
          </Box>
        </Menu>
      </ProSidebar>
      <Plans open={plansModalOpen} onClose={handleClosePlansModal} />
    </Box>
  );
};

export default Sidebar;
